import { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Center,
  Container,
  Flex,
  Heading,
  Icon,
  IconButton,
  Input,
  Spacer,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import {
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { useParams } from 'react-router-dom';
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { toaster } from '@/components/ui/toaster';
import DestructiveModal from '@/components/alert-dialogs/destructive-modal';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import api from '@/utils/api';
import { unwrap } from '@/pages/courses/api';
import { User, WorkspaceGroup } from '@/lib/types';
import GroupMembersPanel from './group-members-panel';

// Workspace groups management (admin-only). The two role-backed defaults
// (Coaches, Viewers) are managed by moving people between them — a
// workspace-role change under the hood. Custom groups get hand-picked
// members from the workspace roster.
export default function ListGroups() {
  const { workspaceId = '' } = useParams<{ workspaceId: string }>();
  const isAdmin = useIsInAdminGroup();

  const [groups, setGroups] = useState<WorkspaceGroup[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openGroupId, setOpenGroupId] = useState<number | null>(null);

  // Create/edit share one dialog; `editing` null means create.
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<WorkspaceGroup | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<WorkspaceGroup | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadGroups = useCallback(async () => {
    const res = await api.get(`groups/workspace/${workspaceId}`);
    setGroups(unwrap<WorkspaceGroup[]>(res) ?? []);
  }, [workspaceId]);

  const loadUsers = useCallback(async () => {
    const res = await api.get(`workspace/${workspaceId}/users`);
    setUsers((res.data.resource as User[]) ?? []);
  }, [workspaceId]);

  // Role moves change both defaults' membership and the roster's role ids,
  // so a change anywhere refreshes both.
  const refreshAll = useCallback(async () => {
    await Promise.all([loadGroups(), loadUsers()]);
  }, [loadGroups, loadUsers]);

  useEffect(() => {
    if (!workspaceId || !isAdmin) return;
    refreshAll()
      .catch(() => {
        toaster.create({ title: 'Failed to load groups', type: 'error' });
      })
      .finally(() => setIsLoading(false));
  }, [workspaceId, isAdmin, refreshAll]);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setDescription('');
    setEditorOpen(true);
  };

  const openEdit = (group: WorkspaceGroup) => {
    setEditing(group);
    setName(group.name);
    setDescription(group.description);
    setEditorOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setIsSaving(true);
    try {
      if (editing) {
        await api.put(`groups/${editing.id}`, {
          name: name.trim(),
          description: description.trim(),
        });
        toaster.create({ title: 'Group updated', type: 'success' });
      } else {
        await api.post('groups', {
          workspace_id: Number(workspaceId),
          name: name.trim(),
          description: description.trim(),
        });
        toaster.create({ title: 'Group created', type: 'success' });
      }
      setEditorOpen(false);
      await loadGroups();
    } catch (error: any) {
      toaster.create({
        title: error.response?.data?.error || 'Failed to save group',
        type: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await api.delete(`groups/${deleteTarget.id}`);
      toaster.create({ title: 'Group deleted', type: 'success' });
      if (openGroupId === deleteTarget.id) setOpenGroupId(null);
      setDeleteTarget(null);
      await loadGroups();
    } catch (error: any) {
      toaster.create({
        title: error.response?.data?.error || 'Failed to delete group',
        type: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isAdmin) {
    return (
      <Center minH='50vh'>
        <Text fontSize='sm' color='fg.muted'>
          Only workspace admins can manage groups.
        </Text>
      </Center>
    );
  }

  return (
    <Box minH='100vh' mx='auto' width={{ md: '90%' }} maxW='6xl' px={{ base: 4, md: 0 }} py={6}>
      <Container>
        <Flex mb={2} alignItems='center' gap={4}>
          <Heading as='h1' size='3xl'>
            Groups
          </Heading>
          <Spacer />
          <Button variant='primary' onClick={openCreate}>
            <Icon as={PlusIcon} w={4} h={4} />
            New group
          </Button>
        </Flex>
        <Text fontSize='sm' color='fg.muted' mb={6} maxW='2xl'>
          Groups are the audiences you assign courses to. Coaches and Viewers follow workspace
          roles — move people between them to change who counts as which. Custom groups hold
          whoever you add.
        </Text>

        {isLoading ? (
          <Center mt={8}>
            <Spinner size='lg' />
          </Center>
        ) : (
          <Stack gap={3}>
            {groups.map((group) => {
              const isOpen = openGroupId === group.id;
              const roleBacked = group.auto_role_id != null;
              return (
                <Box
                  key={group.id}
                  bg='bg.panel'
                  borderWidth='1px'
                  borderColor={isOpen ? 'brand.emphasized' : 'border'}
                  borderRadius='lg'
                  shadow={isOpen ? 'md' : 'sm'}
                  transition='border-color 0.15s, box-shadow 0.15s'
                  _hover={isOpen ? undefined : { shadow: 'md' }}
                >
                  <Flex align='center' gap={3} px={5} py={4}>
                    <Center w='34px' h='34px' borderRadius='full' bg='brand.soft' flexShrink={0}>
                      <Icon as={UserGroupIcon} w={4.5} h={4.5} color='brand.fg' />
                    </Center>
                    <Box minW={0} flex='1'>
                      <Flex align='center' gap={2}>
                        <Text fontSize='sm' fontWeight='600' color='fg.strong'>
                          {group.name}
                        </Text>
                        {roleBacked && (
                          <Badge bg='bg.subtle' color='fg.muted' fontSize='2xs'>
                            Follows role
                          </Badge>
                        )}
                      </Flex>
                      {group.description && (
                        <Text fontSize='xs' color='fg.muted' lineClamp={1}>
                          {group.description}
                        </Text>
                      )}
                    </Box>
                    <Box textAlign='right' flexShrink={0} minW='56px'>
                      <Text fontSize='md' fontWeight='700' color='fg.strong' lineHeight='1.1'>
                        {group.member_count}
                      </Text>
                      <Text
                        fontSize='2xs'
                        color='fg.muted'
                        textTransform='uppercase'
                        letterSpacing='wider'
                      >
                        {group.member_count === 1 ? 'member' : 'members'}
                      </Text>
                    </Box>
                    <Button
                      variant='outline'
                      size='xs'
                      onClick={() => setOpenGroupId(isOpen ? null : group.id)}
                    >
                      {isOpen ? 'Close' : 'Manage members'}
                    </Button>
                    <IconButton
                      aria-label='Edit group'
                      variant='ghost'
                      size='xs'
                      color='fg.subtle'
                      _hover={{ color: 'brand.fg' }}
                      onClick={() => openEdit(group)}
                    >
                      <Icon as={PencilSquareIcon} w={3.5} h={3.5} />
                    </IconButton>
                    {!roleBacked && (
                      <IconButton
                        aria-label='Delete group'
                        variant='ghost'
                        size='xs'
                        color='fg.subtle'
                        _hover={{ color: 'danger' }}
                        onClick={() => setDeleteTarget(group)}
                      >
                        <Icon as={TrashIcon} w={3.5} h={3.5} />
                      </IconButton>
                    )}
                  </Flex>

                  {isOpen && (
                    <Box
                      px={5}
                      pb={5}
                      pt={4}
                      bg='bg.subtle'
                      borderTopWidth='1px'
                      borderColor='border'
                      borderBottomRadius='lg'
                    >
                      <GroupMembersPanel
                        key={group.id}
                        group={group}
                        allGroups={groups}
                        users={users}
                        workspaceId={workspaceId}
                        onChanged={refreshAll}
                      />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}

        <DialogRoot lazyMount open={editorOpen} onOpenChange={() => setEditorOpen(false)}>
          <DialogBackdrop />
          <DialogContent>
            <DialogCloseTrigger />
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit group' : 'New group'}</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <Stack gap={4}>
                <Field label='Group name' required>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder='e.g. Ridgeview Flag U10'
                    maxLength={100}
                  />
                </Field>
                <Field label='Description'>
                  <Input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    maxLength={500}
                  />
                </Field>
              </Stack>
            </DialogBody>
            <DialogFooter>
              <Button variant='outline' size='sm' onClick={() => setEditorOpen(false)}>
                Cancel
              </Button>
              <Button variant='primary' size='sm' onClick={handleSave} loading={isSaving}>
                {editing ? 'Save changes' : 'Create group'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </DialogRoot>

        <DestructiveModal
          isOpen={deleteTarget != null}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          title='Delete group'
          confirmText='Delete'
          isLoading={isDeleting}
        >
          <Text fontSize='sm'>
            Delete <strong>{deleteTarget?.name}</strong>? Course assignments targeting it go with
            it. People keep their accounts and roles.
          </Text>
        </DestructiveModal>
      </Container>
    </Box>
  );
}
